const CANONICAL_ID_CLAIM = 'https://claims.cimpress.io/canonical_id'

const decodeBase64Url = (input: string): string => {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  return atob(padded)
}

/**
 * Extracts the Cimpress canonical id (`https://claims.cimpress.io/canonical_id`)
 * from an authorization header value.
 *
 * Supports `Bearer <jwt>`, `Session <jwt>`, or a bare JWT. Returns `undefined`
 * for opaque (non-JWT) tokens or if the claim is absent/unparseable.
 *
 * Primary use: in local development an authenticated token is injected via
 * `LOCAL_DEV_AUTH_HEADER` while the live browser identity is anonymous. Deriving
 * the shopper id from the injected token keeps the identity used for the
 * `Authorization` header consistent with the `shopperId` sent to shopper-scoped
 * APIs (e.g. the cart), which require the two to match.
 */
export const getCanonicalIdFromAuthHeader = (authorizationHeader: string | undefined): string | undefined => {
  if (!authorizationHeader) return undefined

  const token = authorizationHeader.replace(/^(Bearer|Session)\s+/i, '').trim()
  const parts = token.split('.')
  if (parts.length !== 3) return undefined

  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]!)) as Record<string, unknown>
    const canonicalId = payload[CANONICAL_ID_CLAIM]
    return typeof canonicalId === 'string' ? canonicalId : undefined
  } catch {
    return undefined
  }
}
